'use client';

import {
  AudioLines,
  Box,
  Camera,
  Crosshair,
  Flashlight,
  LocateFixed,
  Package,
  Radar,
  RadioTower,
  Route,
  Speaker,
  Thermometer,
  PlaneTakeoff,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { canLaunchFromBay, presenceView, TONE_TEXT, type BayAirborne } from '@/lib/dock-status';
import { getManufacturerLabel } from '@/lib/utils';
import type { DockBay, DockPresence } from '@/types/dock';
import type { DronePayloadType } from '@/types/fleet';

/** The three launch workflows a selected drone can start. */
export type LaunchMode = 'takeoff' | 'point' | 'route' | 'mission';

interface DockBayRowProps {
  bayIndex: number;
  bay: DockBay | null;
  presence: DockPresence;
  /** Flying state of this bay's drone with its link freshness; false = on the pad. */
  airborne: BayAirborne;
  selected: boolean;
  /** False for viewers or while the dock is offline. */
  canLaunch: boolean;
  /** Why canLaunch is false (role/connectivity), shown when the grid is dead. */
  launchBlockReason: string | null;
  onToggleSelect: () => void;
  onLaunch: (mode: LaunchMode) => void;
  /** Center the COP map on this bay's airborne drone. */
  onGoToDrone: () => void;
}

// Payload icons are structural, never green - green stays a status color.
const PAYLOAD_ICONS: Record<DronePayloadType, LucideIcon> = {
  camera: Camera,
  thermal: Thermometer,
  acoustic: AudioLines,
  lidar: Radar,
  rf: RadioTower,
  speaker: Speaker,
  spotlight: Flashlight,
  delivery: Package,
  other: Box,
};

const LAUNCH_ACTIONS: { mode: LaunchMode; label: string; Icon: LucideIcon }[] = [
  // Takeoff = climb-and-hover over the dock through the full bay sequence —
  // the runbook's sample-takeoff test as one button.
  { mode: 'takeoff', label: 'Takeoff', Icon: PlaneTakeoff },
  { mode: 'point', label: 'To Point', Icon: Crosshair },
  { mode: 'route', label: 'To Route', Icon: Route },
  { mode: 'mission', label: 'Mission', Icon: Box },
];

export function DockBayRow({
  bayIndex,
  bay,
  presence,
  airborne,
  selected,
  canLaunch,
  launchBlockReason,
  onToggleSelect,
  onLaunch,
  onGoToDrone,
}: DockBayRowProps) {
  const presenceState = presenceView(presence, airborne);
  // An empty bay has nothing to launch; a drone reported off the pad or already
  // in the air must not expose launch actions (spec §13).
  const launchable = bay !== null && canLaunch && canLaunchFromBay(presence, airborne);
  // An airborne drone's row stays selectable regardless of command rights or
  // dock connectivity: the only action inside is go-to-drone, which is pure
  // map navigation and as legitimate for a viewer as for an operator. A row
  // that is already open must also stay collapsible even after it stops being
  // selectable (e.g. the drone lands while a viewer has it expanded).
  const selectable = launchable || (bay !== null && airborne !== false);
  const headerInteractive = selectable || selected;
  // The expanded grid always explains its own disabled state — a panel of
  // greyed buttons with no stated cause reads as broken, and the title
  // tooltip alone is unreachable on touch and for keyboard/SR users.
  // No not_in_dock fallback here: presence no longer vetoes a launch, so with
  // the aircraft on the ground `launchable` is already true and the branch was
  // unreachable - it would only ever have printed "launch unavailable" on a row
  // whose launch button works. The dock's on-hub byte is a warning (the NOT IN
  // DOCK chip), not a reason. A real block still states itself via
  // launchBlockReason, which is server-side truth.
  const launchBlockNote =
    airborne !== false
      ? 'Launch unavailable while in air'
      : launchable
        ? null
        : (launchBlockReason ?? null);
  const droneLabel = bay?.callsign ?? bay?.name ?? null;
  // The chip's freshness qualifier must reach screen readers too — the header's
  // aria-label overrides all inner text, so "in the air, link lost" has to be said
  // here or a dead-link drone is announced as a confident live claim.
  //
  // The label is already a comma'd sentence in `lib/copy/dock.ts`, so the old
  // `.replace(' · ', ', ')` that used to repair the separator here is gone with it.
  const airborneAnnouncement =
    airborne === false ? '' : `${presenceState.label.toLowerCase()}, `;
  // Second line: which airframe is actually parked here. A Mavic and an X10 fly
  // nothing alike, so the operator needs the type without leaving the COP.
  // manufacturer/model ride along on the bay payload already — no extra fetch.
  const bayDetail = bay
    ? [
        getManufacturerLabel(bay.manufacturer, bay.model),
        bay.model,
        bay.is_launch_bay ? 'Launch bay' : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'No drone assigned';

  return (
    <div
      className={`rounded-md border transition-colors ${
        selected
          ? 'border-primary/40 bg-primary/10'
          : 'border-border bg-muted/50 ' + (selectable ? 'hover:border-[var(--border-hover)]' : '')
      }`}
    >
      {/* One row: bay · drone · payloads · presence. It wraps rather than
          truncating when the sidebar is at its narrowest, so no element is ever
          hidden — including for a drone that is not on the pad. */}
      <button
        type="button"
        onClick={headerInteractive ? onToggleSelect : undefined}
        disabled={!headerInteractive}
        aria-expanded={headerInteractive ? selected : undefined}
        aria-label={
          // The label overrides the row's inner text for screen readers, so the
          // airframe has to be repeated here or it is announced to nobody —
          // likewise the in-air state, which explains the disabled launch grid.
          droneLabel
            ? `Select ${droneLabel} in bay ${bayIndex}, ${airborneAnnouncement}${bayDetail}`
            : `Bay ${bayIndex} empty`
        }
        className="flex w-full flex-wrap items-center gap-x-2 gap-y-1.5 px-2 py-2 text-left disabled:cursor-default"
      >
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Bay {bayIndex}
        </span>

        <span className="min-w-[4.5rem] flex-1">
          <span
            // Truncates at the side panel's 320 px default where it did not at
            // 560, and unlike the detail line below it had no way to be read in
            // full.
            title={droneLabel ?? undefined}
            className="block truncate text-[12px] font-semibold uppercase text-foreground"
          >
            {droneLabel ?? '—'}
          </span>
          <span
            title={bayDetail}
            className="block truncate text-[9px] uppercase tracking-wide text-muted-foreground"
          >
            {bayDetail}
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-1.5" aria-label="Installed payloads">
          {(bay?.payloads ?? []).map((payload, i) => {
            const Icon = PAYLOAD_ICONS[payload.type] ?? Box;
            return (
              <span
                key={`${payload.type}-${i}`}
                title={payload.name}
                aria-label={payload.name}
                className="grid size-7 shrink-0 place-items-center rounded-md border border-border bg-muted/20 text-muted-foreground"
              >
                <Icon className="size-3.5" strokeWidth={1.5} aria-hidden />
              </span>
            );
          })}
        </span>

        <span
          className={`ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[10px] font-bold uppercase ${TONE_TEXT[presenceState.tone]}`}
        >
          <span className="size-1.5 rounded-full bg-current" aria-hidden />
          {presenceState.label}
        </span>
      </button>

      {launchBlockNote && !headerInteractive && (
        // The expanded grid explains itself, but a row that is launch-blocked is
        // also not selectable (`selectable` is derived from `launchable`), so the
        // operator cannot open the panel that holds the reason. A silent, inert
        // row reads as a broken UI rather than as a stated refusal -- the same
        // shape as the bay-unconfirmed prompt that rendered under the review card.
        // State it on the collapsed row, where it is the only place it can be read.
        <p className="px-2 pb-1.5 text-[9px] uppercase tracking-wide text-muted-foreground">
          {launchBlockNote}
        </p>
      )}

      {selected && bay && (
        <div className="border-t divider p-1.5">
          {/* Grid first, go-to below: go-to unmounts the frame the drone lands,
              which is the same frame launch can re-enable — the grid must not
              shift into a click aimed at the vanishing button. */}
          <div className="grid grid-cols-4 gap-1.5">
            {LAUNCH_ACTIONS.map(({ mode, label, Icon }) => (
              <button
                key={mode}
                type="button"
                onClick={() => onLaunch(mode)}
                // The gate, not just the airborne flag: a row opened while
                // airborne outlives the flight, and landing must not hand
                // launch buttons to a viewer or an offline dock.
                disabled={!launchable}
                title={launchBlockNote ?? undefined}
                className="flex min-h-[3.5rem] flex-col items-center justify-center gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-1 py-2 text-center transition-colors enabled:hover:border-primary/50 enabled:hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon className="size-4 text-status-cyan-fg" strokeWidth={1.5} aria-hidden />
                <span className="text-[9px] font-bold uppercase leading-tight text-foreground">
                  {label}
                </span>
              </button>
            ))}
          </div>
          {airborne !== false && (
            <button
              type="button"
              onClick={onGoToDrone}
              className="mt-1.5 flex h-9 w-full items-center justify-center gap-2 rounded-md border border-status-blue/40 bg-status-blue/10 text-[10px] font-bold uppercase tracking-wide text-status-blue-fg transition-colors hover:bg-status-blue/20"
            >
              <LocateFixed className="size-4" strokeWidth={1.5} aria-hidden />
              Go to drone
            </button>
          )}
          {launchBlockNote && (
            // Visible everywhere the title tooltip is not: touch layouts and
            // keyboard/SR users, who cannot reach a disabled button's title.
            <p className="mt-1.5 text-center text-[9px] uppercase tracking-wide text-muted-foreground">
              {launchBlockNote}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
